import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';
import { ProductGroupRatio } from '@texas/api/endpoints/statisticsApi';
import { useTranslation } from 'react-i18next';
import {
  BranchIdentifier,
  getBranchColor,
} from '@texas/utils/helpers/texas/branchHelpers';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

interface RatioChartProps {
  data: ProductGroupRatio[];
}

export function RatioChart({ data }: RatioChartProps) {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const allBranches = new Set<string>();
    const branchIdentifierMap = new Map<string, string>();

    data.forEach((pg) => {
      pg.branches.forEach((b) => {
        allBranches.add(b.branchName);
        branchIdentifierMap.set(b.branchName, b.branchIdentifier);
      });
    });

    const branchNames = Array.from(allBranches);
    const labels = data.map((pg) => pg.productGroupName);

    const datasets = branchNames.map((branchName) => {
      const branchIdentifier = branchIdentifierMap.get(
        branchName,
      ) as BranchIdentifier;
      const branchColor = getBranchColor(branchIdentifier);

      const branchData = data.map((pg) => {
        const branch = pg.branches.find((b) => b.branchName === branchName);
        return branch ? branch.averageRatio : 0;
      });

      const cssVar = branchColor.match(/var\((--chakra-colors-[^)]+)\)/)?.[1];
      let backgroundColor = branchColor;
      let borderColor = branchColor;

      if (cssVar) {
        const computedColor = getComputedStyle(document.documentElement)
          .getPropertyValue(
            cssVar.replace('--chakra-colors-', '--chakra-colors-'),
          )
          .trim();

        if (computedColor) {
          backgroundColor = computedColor;
          borderColor = computedColor;
        }
      }

      return {
        label: branchName,
        data: branchData,
        backgroundColor,
        borderColor,
        borderWidth: 1,
      };
    });

    return { labels, datasets };
  }, [data]);

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: false,
          title: {
            display: true,
            text: t('widgets.paRatio.productGroup'),
          },
        },
        y: {
          stacked: false,
          title: {
            display: true,
            text: t('widgets.paRatio.avgRatio'),
          },
          beginAtZero: true,
        },
      },
      plugins: {
        legend: {
          position: 'top' as const,
          labels: {
            boxWidth: 12,
            font: {
              size: 11,
            },
          },
        },
        title: {
          display: true,
          text: t('widgets.paRatio.chartTitle'),
        },
        tooltip: {
          callbacks: {
            label: (context: any) => {
              const branchName = context.dataset.label;
              const ratio = context.parsed.y.toFixed(2);

              const productGroup = data[context.dataIndex];
              const branch = productGroup.branches.find(
                (b) => b.branchName === branchName,
              );
              const count = branch?.compositionGroupCount ?? 0;

              return t('widgets.paRatio.tooltipLabel', {
                branchName,
                ratio,
                count,
              });
            },
          },
        },
        annotation: {
          annotations: {
            line1: {
              type: 'line',
              yMin: 1,
              yMax: 1,
              borderColor: 'rgb(255, 99, 132)',
              borderWidth: 2,
              borderDash: [5, 5],
              label: {
                display: true,
                content: t('widgets.paRatio.perfectRatio'),
                position: 'end',
                backgroundColor: 'rgba(255, 99, 132, 0.8)',
                color: 'white',
                font: {
                  size: 10,
                },
              },
            },
          },
        },
      },
    }),
    [data, t],
  );

  return (
    <Box height="400px" width="100%">
      <Bar data={chartData} options={options} />
    </Box>
  );
}
